import type { NotificationChannelView } from '@occ/contracts';
import { useEffect, useState } from 'react';
import { listNotificationChannels } from '../data/notification-channels';

/**
 * Los canales de notificación del tenant, para elegir adónde salen los avisos de
 * una ficha de nodo. Lo comparten el interruptor de avisos y las vigilancias de
 * procesos: dos tarjetas que antes cargaban la misma lista cuatro veces.
 *
 * Sin permiso o sin backend la lista queda vacía: el motivo ya lo explica el
 * permiso sobre los controles, y esconder el error aquí no cambia lo que el
 * operador puede hacer.
 */
export function useNotificationChannels(): NotificationChannelView[] {
  const [channels, setChannels] = useState<NotificationChannelView[]>([]);

  useEffect(() => {
    let cancelled = false;
    listNotificationChannels()
      .then((list) => {
        if (!cancelled) setChannels(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return channels;
}

/**
 * Canales como fichas en una sola línea que se parte, y no como una columna de
 * casillas: con tres interruptores y varias vigilancias en la misma ficha, una
 * lista vertical por cada uno era lo que estiraba la pantalla.
 */
export function ChannelPicker({
  channels,
  selected,
  disabled,
  onToggle,
}: {
  channels: NotificationChannelView[];
  selected: ReadonlySet<string>;
  disabled?: boolean;
  onToggle: (id: string) => void;
}) {
  if (channels.length === 0) {
    return (
      <p className="text-xs opacity-60">
        No hay canales configurados todavía — créalos en Administración → Canales de notificación.
        Mientras tanto la alerta solo queda en el panel.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {channels.map((channel) => {
        const on = selected.has(channel.id);
        return (
          <button
            key={channel.id}
            type="button"
            disabled={disabled}
            aria-pressed={on}
            onClick={() => onToggle(channel.id)}
            className={[
              'rounded-full px-2.5 py-1 text-xs shadow-sm transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-50',
              on ? 'bg-primary text-primary-foreground' : 'bg-accent/50 hover:bg-accent',
            ].join(' ')}
          >
            {channel.name} <span className="opacity-60">· {channel.kind}</span>
          </button>
        );
      })}
    </div>
  );
}
